import { useRef, useState, useEffect } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons';
import { Button, Input } from '@/components/common';
import { mergeClasses } from '@/utils';
import { NON_NUMERIC_REGEX } from '@/constants';

import { IPagination } from './Pagination.interfaces';

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  className,
}: IPagination) => {
  const pageInputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if viewport is less than 640px
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalItems);

  const onChangePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(NON_NUMERIC_REGEX, '');
    if (value === '') {
      return pageInputRef.current && (pageInputRef.current.value = '');
    }

    // Clamp value between 1 and totalPages
    const numericValue = Math.max(1, Math.min(totalPages, Number(value)));
    if (pageInputRef.current && !isNaN(numericValue)) {
      pageInputRef.current.value = numericValue.toString();
    }
  };

  const onGoToPage = () => {
    const inputValue = pageInputRef.current?.value || '';
    const pageNum = parseInt(inputValue, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      if (pageInputRef.current) {
        pageInputRef.current.value = '';
      }
    }
  };

  // Generate page numbers to display based on viewport width
  const getPageNumbers = (): (number | string)[] => {
    if (isMobile) {
      // Mobile view: < 640px
      if (totalPages <= 4) {
        // Show all pages if total is 4 or less
        const pages: (number | string)[] = [];
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
        return pages;
      }

      // At corners: show 3 pages + last [1, 2, 3, ..., last]
      if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages];
      }

      // Near end: show first + last 3 pages [1, ..., last-2, last-1, last]
      if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 2, totalPages - 1, totalPages];
      }

      // Middle: show only current page [1, ..., current, ..., last]
      return [1, '...', currentPage, '...', totalPages];
    } else {
      // Desktop view: >= 640px (existing logic)
      const maxVisible = 6;

      if (totalPages <= maxVisible) {
        // Show all pages if total is 6 or less
        const pages: (number | string)[] = [];
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
        return pages;
      }

      // For more than 6 pages, we always show exactly 6 slots
      // Pattern: [1] [...] [middle pages] [...] [last]
      // or sliding window when near edges

      const pages: (number | string)[] = [];

      if (currentPage <= 3) {
        // Near start: show [1, 2, 3, 4, ..., last]
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near end: show [1, ..., last-3, last-2, last-1, last]
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle: show [1, ..., current-1, current, current+1, ..., last]
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }

      return pages;
    }
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={mergeClasses(
        'flex flex-wrap gap-2 items-center justify-between py-m text-sm',
        className,
      )}
    >
      {/* Left: Record count */}
      <div className="text-neutral-n7">
        {startRecord} to {endRecord} of {totalItems} records
      </div>

      {/* Center: Pagination controls */}
      <div className="flex items-center gap-s">
        {/* Previous button  */}
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          variant="secondary"
          borderRadius="soft"
          size="sm"
          className="h-l w-l text-brand-primary-darkest border-neutral-n4 !rounded focus:shadow-none disabled:bg-neutral-white"
          prefixIcon={<ChevronLeftIcon className="size-base" />}
        />

        {/* Page numbers */}
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="flex h-l w-l items-center justify-center text-neutral-n8"
              >
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === currentPage;

          return (
            <Button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              variant="secondary"
              borderRadius="soft"
              size="sm"
              className={mergeClasses(
                'h-l min-w-l border-neutral-n4 !rounded focus:shadow-none',
                isActive
                  ? '!border-brand-primary-base !bg-brand-primary-lightest text-brand-primary-base font-medium'
                  : 'text-brand-primary-darkest hover:bg-neutral-n2',
              )}
            >
              {pageNum}
            </Button>
          );
        })}

        {/* Next button */}
        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          variant="secondary"
          borderRadius="soft"
          size="sm"
          className="h-l w-l text-brand-primary-darkest border-neutral-n4 !rounded focus:shadow-none disabled:bg-neutral-white"
          prefixIcon={<ChevronRightIcon className="size-base" />}
        />
      </div>

      {/* Right: Go to page */}
      <div className="flex items-center gap-s">
        <span className="text-neutral-n7">Go to page</span>
        <Input
          ref={pageInputRef}
          onChange={onChangePageInput}
          className="[&_input]:h-l !w-xxl [&_input]:px-s-plus [&_input]:text-center [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:[-moz-appearance:textfield]"
          placeholder=""
        />
        <Button
          variant="primary"
          className="w-xl h-l"
          size="sm"
          borderRadius="soft"
          onClick={onGoToPage}
        >
          Go
        </Button>
      </div>
    </div>
  );
};

export { Pagination, type IPagination };
